/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/clamp.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Limits (or clamps) the values of all samples to have a lower limit of min and an upper limit of max.
 *
 * @example
 * TS k8s
 * | EVAL full_clamped_cost = clamp(network.cost, clamp_max(network.bytes_in, 5), network.bytes_in / 100)
 * | KEEP full_clamped_cost, @timestamp
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'clamp',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'min',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'max',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'min',
          type: 'date',
          optional: false,
        },
        {
          name: 'max',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'date',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'min',
          type: 'double',
          optional: false,
        },
        {
          name: 'max',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'min',
          type: 'integer',
          optional: false,
        },
        {
          name: 'max',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'min',
          type: 'long',
          optional: false,
        },
        {
          name: 'max',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'min',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'max',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'min',
          type: 'double',
          optional: false,
        },
        {
          name: 'max',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'min',
          type: 'integer',
          optional: false,
        },
        {
          name: 'max',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'integer',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'min',
          type: 'long',
          optional: false,
        },
        {
          name: 'max',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'min',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'max',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'min',
          type: 'ip',
          optional: false,
        },
        {
          name: 'max',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'ip',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'min',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'max',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'keyword',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'min',
          type: 'double',
          optional: false,
        },
        {
          name: 'max',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'min',
          type: 'integer',
          optional: false,
        },
        {
          name: 'max',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'min',
          type: 'long',
          optional: false,
        },
        {
          name: 'max',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'min',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'max',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'min',
          type: 'double',
          optional: false,
        },
        {
          name: 'max',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'double',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'min',
          type: 'integer',
          optional: false,
        },
        {
          name: 'max',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'min',
          type: 'long',
          optional: false,
        },
        {
          name: 'max',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'min',
          type: 'unsigned_long',
          optional: false,
        },
        {
          name: 'max',
          type: 'unsigned_long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'unsigned_long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'min',
          type: 'version',
          optional: false,
        },
        {
          name: 'max',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'version',
    },
  ],
  preview: true,
  snapshotOnly: false,
};

export default definition;
